import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, CreditCard, Lock, QrCode, FileText, ChevronRight, User, Banknote, ShieldCheck, Check, Cpu, Wifi, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { SEO } from '../components/SEO';
import { PixPaymentInline, type PixShape } from '../components/marketing/PixPaymentModal';
import { publicSaasSignupService } from '../services/api';
import {
  clearCheckoutDraft,
  loadCheckoutDraft,
  saveCheckoutDraft,
} from '../utils/checkoutDraftSession';
import { getCheckoutBootstrap } from '../utils/checkoutBootstrap';
import {
  clearPendingCheckout,
  getChargedCheckoutPlanPeriod,
  loadPendingCheckout,
  needsCheckoutPlanChangeConfirm,
  savePendingCheckout,
  type PendingCheckoutSnapshot,
} from '../utils/checkoutPendingSession';
import { PlanChangeConfirmDialog } from '../components/marketing/PlanChangeConfirmDialog';
import { SAAS_CHECKOUT_CREDIT_CARD_ENABLED } from '../config/saasCheckoutOptions';
import { usePublicSaasPlans } from '../hooks/usePublicSaasPlans';
import { computeSaasCheckoutPricing } from '../utils/saasPlanPricing';

const periodLabels: Record<string, string> = {
  mensal: 'Mensal',
  trimestral: 'Trimestral',
  semestral: 'Semestral',
  anual: 'Anual',
};

type SignupResult = {
  continuationToken?: string;
  immediateActive?: boolean;
  pix?: PixShape | null;
  bankSlipUrl?: string | null;
  invoiceUrl?: string | null;
  paymentId?: string | null;
  subscriptionId?: string | null;
};

function formatOrderRef(ids: {
  paymentId?: string | null;
  subscriptionId?: string | null;
  tenantKey?: string;
}): string {
  const raw = ids.paymentId || ids.subscriptionId;
  if (raw) {
    const tail = String(raw).replace(/\W/g, '').slice(-8).toUpperCase();
    return `#RT-${tail}`;
  }
  const tk = (ids.tenantKey || '').replace(/\D/g, '');
  if (tk.length >= 4) return `#RT-${tk.slice(-6)}`;
  return '#RT-PENDENTE';
}

export function Checkout() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const planId = searchParams.get('plan') || 'v4';
  const period = searchParams.get('period') || 'mensal';

  const { plans, loading: plansLoading } = usePublicSaasPlans();

  const selectedPlan = useMemo(
    () => plans.find((p) => p.id === planId) ?? plans[0],
    [plans, planId]
  );

  const { discount, total } = useMemo(
    () => computeSaasCheckoutPricing(selectedPlan, period),
    [selectedPlan, period]
  );

  const checkoutBoot = useRef(getCheckoutBootstrap(planId, period)).current;
  const initialPaymentMethod =
    checkoutBoot.paymentMethod === 'credit_card' && !SAAS_CHECKOUT_CREDIT_CARD_ENABLED
      ? null
      : checkoutBoot.paymentMethod;
  const [step, setStep] = useState<1 | 2 | 3>(checkoutBoot.step);
  /** Passo 2 sem aba marcada; clique em PIX/Boleto seleciona e cobra. */
  const [paymentMethod, setPaymentMethod] = useState<'credit_card' | 'pix' | 'boleto' | null>(
    initialPaymentMethod
  );

  // Identification State
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [step1Error, setStep1Error] = useState('');
  const [documentFieldError, setDocumentFieldError] = useState('');
  const [emailFieldError, setEmailFieldError] = useState('');
  const [checkingStep1, setCheckingStep1] = useState(false);
  
  // Card form state
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [step2Error, setStep2Error] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [holderPostalCode, setHolderPostalCode] = useState('');
  const [holderAddressNumber, setHolderAddressNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pixAwaitingPayment, setPixAwaitingPayment] = useState(false);
  /** Meio usado na cobrança já gerada (PIX/boleto aguardando). */
  const [chargedPaymentMethod, setChargedPaymentMethod] = useState<
    'credit_card' | 'pix' | 'boleto' | null
  >(null);
  const [continuationToken, setContinuationToken] = useState('');
  const [pixPayload, setPixPayload] = useState<PixShape | null>(null);
  const [bankSlipUrl, setBankSlipUrl] = useState<string | null>(null);
  const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null);
  const chargeInFlight = useRef(false);
  const checkoutRestored = useRef(false);
  const [isRestoringCheckout, setIsRestoringCheckout] = useState(checkoutBoot.isRestoring);
  const [completedOrderRef, setCompletedOrderRef] = useState('');
  const [lastSignupPaymentId, setLastSignupPaymentId] = useState<string | null>(null);
  const [lastSignupSubscriptionId, setLastSignupSubscriptionId] = useState<string | null>(null);
  const [planChangeDialog, setPlanChangeDialog] = useState<{
    nextPlanId: string;
    nextPeriod: string;
  } | null>(null);
  const [planChangeLoading, setPlanChangeLoading] = useState(false);

  const clearCheckoutTokenFromUrl = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('token');
        return next;
      },
      { replace: true }
    );
  };

  const syncCheckoutTokenToUrl = (token: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('token', token);
        return next;
      },
      { replace: true }
    );
  };

  const goToCompletionStep = (ids: { paymentId?: string | null; subscriptionId?: string | null }) => {
    setCompletedOrderRef(
      formatOrderRef({ ...ids, tenantKey: documentId.replace(/\D/g, '') })
    );
    setPixAwaitingPayment(false);
    setChargedPaymentMethod(null);
    setSubmitting(false);
    clearPendingCheckout();
    clearCheckoutDraft();
    clearCheckoutTokenFromUrl();
    setStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetAwaitingPayment = () => {
    setPixAwaitingPayment(false);
    setChargedPaymentMethod(null);
    setContinuationToken('');
    setPixPayload(null);
    setBankSlipUrl(null);
    setInvoiceUrl(null);
    clearPendingCheckout();
    clearCheckoutTokenFromUrl();
  };

  const applyCheckoutDraftFields = (draft: ReturnType<typeof loadCheckoutDraft>) => {
    if (!draft) return;
    if (draft.companyName) setCompanyName(draft.companyName);
    if (draft.name) setName(draft.name);
    if (draft.email) setEmail(draft.email);
    if (draft.phone) setPhone(draft.phone);
    if (draft.documentId) setDocumentId(draft.documentId);
  };

  const applyPendingPaymentFromStorage = (
    token: string,
    stored: PendingCheckoutSnapshot,
    pix: PixShape | null,
    slip: string | null,
    invoice: string | null
  ) => {
    const pm = stored.chargedPaymentMethod;
    setContinuationToken(token);
    setChargedPaymentMethod(pm);
    setPaymentMethod(pm);
    setPixPayload(pix);
    setBankSlipUrl(slip);
    setInvoiceUrl(invoice);
    setPixAwaitingPayment(true);
    setStep(2);
    checkoutRestored.current = true;
    syncCheckoutTokenToUrl(token);
    savePendingCheckout({
      continuationToken: token,
      chargedPaymentMethod: pm,
      planId,
      period,
      pixPayload: pix,
      bankSlipUrl: slip,
      invoiceUrl: invoice,
    });
  };

  const showInlinePayment =
    pixAwaitingPayment &&
    !!continuationToken &&
    chargedPaymentMethod != null &&
    paymentMethod === chargedPaymentMethod &&
    (paymentMethod === 'pix' || paymentMethod === 'boleto');

  // Move back to top on step change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  useEffect(() => {
    if (isRestoringCheckout) return;
    const hasAny = Boolean(
      companyName.trim() || name.trim() || email.trim() || phone.trim() || documentId.trim()
    );
    if (!hasAny && step === 1) return;
    saveCheckoutDraft({
      step: step === 2 ? 2 : 1,
      planId,
      period,
      companyName,
      name,
      email,
      phone,
      documentId,
    });
  }, [step, planId, period, companyName, name, email, phone, documentId, isRestoringCheckout]);

  useEffect(() => {
    let cancelled = false;
    const finishRestore = () => {
      if (!cancelled) setIsRestoringCheckout(false);
    };

    const restoreDraftOnly = () => {
      const draft = loadCheckoutDraft();
      if (draft && draft.planId === planId && draft.period === period) {
        applyCheckoutDraftFields(draft);
        if (draft.step === 2) setStep(2);
      }
    };

    const run = async () => {
      const urlToken = new URLSearchParams(window.location.search).get('token');
      const stored = loadPendingCheckout();
      const token = urlToken || stored?.continuationToken;
      if (!token) {
        restoreDraftOnly();
        finishRestore();
        return;
      }
      if (stored && (stored.planId !== planId || stored.period !== period)) {
        clearPendingCheckout();
        if (urlToken) clearCheckoutTokenFromUrl();
        restoreDraftOnly();
        finishRestore();
        return;
      }
      try {
        const res = await publicSaasSignupService.signupStatus(token);
        if (cancelled) return;
        const data = res.data?.data as {
          state?: string;
          pix?: PixShape | null;
          bankSlip?: { bankSlipUrl?: string | null; invoiceUrl?: string | null } | null;
        };
        if (data?.state === 'active') {
          clearPendingCheckout();
          clearCheckoutDraft();
          goToCompletionStep({});
          return;
        }
        if (data?.state === 'pending' || data?.state === 'failed') {
          const nextPix = (data?.pix as PixShape) || stored?.pixPayload || null;
          const nextSlip = data?.bankSlip?.bankSlipUrl || stored?.bankSlipUrl || null;
          const nextInvoice = data?.bankSlip?.invoiceUrl || stored?.invoiceUrl || null;
          const pm: 'pix' | 'boleto' =
            stored?.chargedPaymentMethod || (data?.bankSlip?.bankSlipUrl ? 'boleto' : 'pix');
          if (stored) {
            applyPendingPaymentFromStorage(token, { ...stored, chargedPaymentMethod: pm }, nextPix, nextSlip, nextInvoice);
          } else {
            setContinuationToken(token);
            setChargedPaymentMethod(pm);
            setPaymentMethod(pm);
            setPixPayload(nextPix);
            setBankSlipUrl(nextSlip);
            setInvoiceUrl(nextInvoice);
            setPixAwaitingPayment(true);
            setStep(2);
            checkoutRestored.current = true;
            syncCheckoutTokenToUrl(token);
            savePendingCheckout({
              continuationToken: token,
              chargedPaymentMethod: pm,
              planId,
              period,
              pixPayload: nextPix,
              bankSlipUrl: nextSlip,
              invoiceUrl: nextInvoice,
            });
          }
          applyCheckoutDraftFields(loadCheckoutDraft());
        } else if (
          stored &&
          (stored.pixPayload || stored.bankSlipUrl || stored.continuationToken)
        ) {
          applyPendingPaymentFromStorage(
            token,
            stored,
            stored.pixPayload || null,
            stored.bankSlipUrl || null,
            stored.invoiceUrl || null
          );
          applyCheckoutDraftFields(loadCheckoutDraft());
        } else {
          clearPendingCheckout();
          if (urlToken) clearCheckoutTokenFromUrl();
          restoreDraftOnly();
        }
      } catch {
        if (stored?.continuationToken) {
          applyPendingPaymentFromStorage(
            token,
            stored,
            stored.pixPayload || null,
            stored.bankSlipUrl || null,
            stored.invoiceUrl || null
          );
          applyCheckoutDraftFields(loadCheckoutDraft());
        } else {
          if (urlToken) clearCheckoutTokenFromUrl();
          restoreDraftOnly();
        }
      } finally {
        finishRestore();
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length <= 11) {
      value = value.replace(/(\d{3})(\d)/, '$1.$2')
                   .replace(/(\d{3})(\d)/, '$1.$2')
                   .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    } else {
      value = value.substring(0, 14)
                   .replace(/(\d{2})(\d)/, '$1.$2')
                   .replace(/(\d{3})(\d)/, '$1.$2')
                   .replace(/(\d{3})(\d)/, '$1/$2')
                   .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    }
    setDocumentId(value);
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '').substring(0, 16);
    value = value.replace(/(\d{4})(?=\d)/g, '$1 ');
    setCardNumber(value);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '').substring(0, 11);
    if (value.length > 2) {
      value = `(${value.substring(0, 2)}) ${value.substring(2)}`;
    }
    if (value.length > 10) {
      value = `${value.substring(0, 10)}-${value.substring(10)}`;
    }
    setPhone(value);
  };

  const handleNextStep2 = async (methodOverride?: 'credit_card' | 'pix' | 'boleto') => {
    const method = methodOverride ?? paymentMethod;
    setStep2Error('');
    if (!method) {
      setStep2Error('Selecione uma forma de pagamento (PIX, Boleto ou Cartão).');
      return;
    }
    if (method === 'credit_card') {
      if (!cardNumber || !cardName || !cardExpiry || !cardCvv) {
        setStep2Error('Preencha todos os dados do cartão.');
        return;
      }
      if (cardNumber.replace(/\s/g, '').length < 15) {
        setStep2Error('Número do cartão inválido.');
        return;
      }
      const cep = holderPostalCode.replace(/\D/g, '');
      if (cep.length !== 8) {
        setStep2Error('Informe o CEP do titular do cartão (8 dígitos).');
        return;
      }
      if (!holderAddressNumber.trim()) {
        setStep2Error('Informe o número do endereço do titular.');
        return;
      }
    }
    await submitSignup(method);
  };

  const applyPlanPeriodToUrl = (nextPlan: string, nextPeriod: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('plan', nextPlan);
        next.set('period', nextPeriod);
        return next;
      },
      { replace: true }
    );
  };

  const requestPlanOrPeriodChange = (nextPlanId: string, nextPeriod: string) => {
    if (needsCheckoutPlanChangeConfirm(nextPlanId, nextPeriod, pixAwaitingPayment)) {
      setPlanChangeDialog({ nextPlanId, nextPeriod });
      return;
    }
    applyPlanPeriodToUrl(nextPlanId, nextPeriod);
  };

  const cancelPlanChangeDialog = () => setPlanChangeDialog(null);

  const confirmPlanChangeDialog = async () => {
    if (!planChangeDialog || !chargedPaymentMethod) return;
    const { nextPlanId, nextPeriod } = planChangeDialog;
    const method = chargedPaymentMethod;
    setPlanChangeLoading(true);
    setPlanChangeDialog(null);
    resetAwaitingPayment();
    applyPlanPeriodToUrl(nextPlanId, nextPeriod);
    setPaymentMethod(method);
    await submitSignup(method, { planId: nextPlanId, period: nextPeriod });
    setPlanChangeLoading(false);
  };

  const submitSignup = async (
    methodOverride?: 'credit_card' | 'pix' | 'boleto',
    planPeriodOverride?: { planId: string; period: string }
  ) => {
    const pm = methodOverride ?? paymentMethod;
    if (!pm) {
      setStep2Error('Selecione uma forma de pagamento.');
      return;
    }
    const effectivePlanId = planPeriodOverride?.planId ?? planId;
    const effectivePeriod = planPeriodOverride?.period ?? period;
    const digitsDoc = documentId.replace(/\D/g, '');
    if (digitsDoc.length !== 11 && digitsDoc.length !== 14) {
      setStep2Error('CPF ou CNPJ inválido.');
      return;
    }
    const MIN_OVERLAY_MS = 1400;
    const startedAt = Date.now();
    setSubmitting(true);
    setStep2Error('');
    try {
      const resume = searchParams.get('resume') === '1';
      const body: Record<string, unknown> = {
        tenant_key: digitsDoc,
        display_name: companyName.trim(),
        admin_name: name.trim(),
        admin_email: email.trim(),
        admin_password: password,
        phone: phone.replace(/\D/g, ''),
        address: '',
        planId: effectivePlanId,
        period: effectivePeriod,
        paymentMethod: pm,
        holderPostalCode: holderPostalCode.replace(/\D/g, ''),
        holderAddressNumber: holderAddressNumber.trim() || 'S/N',
        resume,
      };
      if (pm === 'credit_card') {
        body.creditCard = {
          holderName: cardName.trim(),
          number: cardNumber.replace(/\s/g, ''),
          expiry: cardExpiry.trim(),
          ccv: cardCvv.trim(),
        };
      }
      const res = await publicSaasSignupService.signup(body);
      const d = res.data?.data as SignupResult;
      const token = d?.continuationToken;
      if (!token) {
        setStep2Error('Resposta inválida do servidor.');
        setSubmitting(false);
        return;
      }
      const elapsed = Date.now() - startedAt;
      await new Promise((r) => setTimeout(r, Math.max(0, MIN_OVERLAY_MS - elapsed)));

      /** Só “pago na hora” no fluxo cartão quando o Asaas devolve assinatura ACTIVE. */
      setLastSignupPaymentId(d?.paymentId || null);
      setLastSignupSubscriptionId(d?.subscriptionId || null);

      const instantOk = pm === 'credit_card' && Boolean(d?.immediateActive);
      if (instantOk) {
        goToCompletionStep({
          paymentId: d?.paymentId,
          subscriptionId: d?.subscriptionId,
        });
        return;
      }

      /** Pix/boleto: QR/copia e cola no passo 2 (sem pop-up); após confirmar → passo 3. */
      if (pm === 'pix' || pm === 'boleto') {
        const nextPix = (d?.pix as PixShape) || null;
        const nextSlip = d?.bankSlipUrl || null;
        const nextInvoice = d?.invoiceUrl || null;
        setContinuationToken(token);
        setPixPayload(nextPix);
        setBankSlipUrl(nextSlip);
        setInvoiceUrl(nextInvoice);
        setChargedPaymentMethod(pm);
        setPaymentMethod(pm);
        setSubmitting(false);
        setPixAwaitingPayment(true);
        syncCheckoutTokenToUrl(token);
        savePendingCheckout({
          continuationToken: token,
          chargedPaymentMethod: pm,
          planId: effectivePlanId,
          period: effectivePeriod,
          pixPayload: nextPix,
          bankSlipUrl: nextSlip,
          invoiceUrl: nextInvoice,
        });
        return;
      }

      navigate(`/checkout/aguardando?token=${encodeURIComponent(token)}`, {
        state: { pixPayload: d?.pix || null },
      });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Não foi possível concluir o cadastro. Tente novamente.';
      setStep2Error(msg);
      setSubmitting(false);
    }
  };

  const activateInstantPayment = async (method: 'pix' | 'boleto') => {
    if (isRestoringCheckout || step !== 2 || chargeInFlight.current || submitting) return;
    if (pixAwaitingPayment && chargedPaymentMethod === method) return;

    if (method !== paymentMethod && pixAwaitingPayment) {
      resetAwaitingPayment();
    }

    setPaymentMethod(method);
    setStep2Error('');
    chargeInFlight.current = true;
    try {
      await handleNextStep2(method);
    } finally {
      chargeInFlight.current = false;
    }
  };

  const selectCardPayment = () => {
    if (paymentMethod !== 'credit_card' && pixAwaitingPayment) {
      resetAwaitingPayment();
    }
    setPaymentMethod('credit_card');
    setStep2Error('');
  };

  const getPasswordStrength = (pwd: string) => {
    if (!pwd) return 0;
    let score = 0;
    if (pwd.length > 5) score += 1;
    if (pwd.length >= 8) score += 1;
    if (/[A-Z]/.test(pwd) && /[0-9]/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
    return Math.min(score, 4);
  };

  const strength = getPasswordStrength(password);
  const strengthColors = ['bg-slate-200', 'bg-red-500', 'bg-orange-500', 'bg-emerald-400', 'bg-green-600'];
  const strengthLabels = ['', 'Fraca', 'Razoável', 'Boa', 'Forte'];

  const getCardBrand = (number: string) => {
    const cleanNumber = number.replace(/\s+/g, '');
    if (/^4/.test(cleanNumber)) return 'VISA';
    if (/^5[1-5]/.test(cleanNumber)) return 'MASTERCARD';
    if (/^3[47]/.test(cleanNumber)) return 'AMEX';
    if (/^3[068]/.test(cleanNumber)) return 'DINERS';
    if (/^6(?:011|5)/.test(cleanNumber)) return 'DISCOVER';
    if (/^(509091|650033|6500|6504|6505|6506|6507|6509|6516|6550)/.test(cleanNumber)) return 'ELO';
    return '';
  };

  const cardBrand = getCardBrand(cardNumber);

  const paymentAreaBusy = submitting;
  const restorePaymentVariant: 'pix' | 'boleto' =
    paymentMethod === 'boleto' || loadPendingCheckout()?.chargedPaymentMethod === 'boleto'
      ? 'boleto'
      : 'pix';

  const renderPaymentAreaLoader = (variant: 'pix' | 'boleto') => {
    const isPix = variant === 'pix';
    return (
      <div
        className={`min-h-[300px] rounded-xl border flex flex-col items-center justify-center p-8 text-center animate-in fade-in duration-300 ${
          isPix ? 'border-teal-100 bg-teal-50/50' : 'border-slate-200 bg-slate-50'
        }`}
        role="status"
        aria-live="polite"
      >
        <Loader2 className={`w-10 h-10 animate-spin mb-4 ${isPix ? 'text-teal-600' : 'text-slate-600'}`} />
        <p className="font-semibold text-slate-900">
          {isRestoringCheckout ? 'Restaurando seu pagamento…' : 'Gerando seu pagamento…'}
        </p>
        <p className="text-sm text-slate-600 mt-2 max-w-md">
          {isRestoringCheckout
            ? 'Recuperando o QR Code ou boleto nesta área.'
            : isPix
              ? 'O QR Code e o Pix copia e cola aparecerão aqui em instantes.'
              : 'O boleto será exibido aqui em instantes.'}
        </p>
      </div>
    );
  };

  // Common input classes
  const inputElClass = "w-full h-12 px-4 border border-blue-200 rounded-xl focus:ring-4 focus:ring-blue-600/20 focus:border-blue-600 transition-all bg-blue-50/70 text-slate-900 font-semibold placeholder:font-normal placeholder:text-slate-400 hover:border-blue-300 outline-none";

  const inputWithFieldError = (hasError: boolean) =>
    `${inputElClass}${hasError ? ' border-red-400 bg-red-50/80 focus:border-red-500 focus:ring-red-500/20' : ''}`;

  const checkSignupFields = async (opts?: { documentOnly?: boolean; emailOnly?: boolean }) => {
    const digits = documentId.replace(/\D/g, '');
    const mail = email.trim().toLowerCase();
    const checkDoc = !opts?.emailOnly;
    const checkMail = !opts?.documentOnly;

    if (checkDoc && digits.length > 0 && digits.length !== 11 && digits.length !== 14) {
      setDocumentFieldError('CPF ou CNPJ inválido.');
      return false;
    }
    if (checkMail && mail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) {
      setEmailFieldError('E-mail inválido.');
      return false;
    }
    if (checkDoc && digits.length !== 11 && digits.length !== 14) return true;
    if (checkMail && !mail) return true;

    try {
      const res = await publicSaasSignupService.checkSignupAvailability(
        checkDoc ? digits : '',
        checkMail ? mail : ''
      );
      const data = res.data?.data as {
        document?: { available?: boolean; message?: string | null };
        email?: { available?: boolean; message?: string | null };
      };
      let ok = true;
      if (checkDoc) {
        if (!data?.document?.available) {
          setDocumentFieldError(data?.document?.message || 'CPF/CNPJ já cadastrado.');
          ok = false;
        } else setDocumentFieldError('');
      }
      if (checkMail) {
        if (!data?.email?.available) {
          setEmailFieldError(data?.email?.message || 'E-mail já cadastrado.');
          ok = false;
        } else setEmailFieldError('');
      }
      return ok;
    } catch {
      setStep1Error('Não foi possível verificar CPF/CNPJ ou e-mail. Tente novamente.');
      return false;
    }
  };

  const handleDocumentBlur = () => {
    const digits = documentId.replace(/\D/g, '');
    if (!digits) {
      setDocumentFieldError('');
      return;
    }
    void checkSignupFields({ documentOnly: true });
  };

  const handleEmailBlur = () => {
    const mail = email.trim();
    if (!mail) {
      setEmailFieldError('');
      return;
    }
    setEmail(email.trim().toLowerCase());
    void checkSignupFields({ emailOnly: true });
  };

  const handleNextStep1 = async () => {
    setStep1Error('');
    setDocumentFieldError('');
    setEmailFieldError('');
    if (!companyName.trim() || !name || !documentId || !email || !phone || !password || !confirmPassword) {
      setStep1Error('Preencha todos os campos para prosseguir (incluindo nome da empresa).');
      return;
    }
    if (password !== confirmPassword) {
      setStep1Error('As senhas não coincidem. Verifique e tente novamente.');
      return;
    }
    if (password.length < 6) {
      setStep1Error('A senha deve ter no mínimo 6 caracteres.');
      return;
    }
    const digits = documentId.replace(/\D/g, '');
    if (digits.length !== 11 && digits.length !== 14) {
      setDocumentFieldError('CPF ou CNPJ inválido.');
      return;
    }
    setCheckingStep1(true);
    const available = await checkSignupFields();
    setCheckingStep1(false);
    if (!available) return;
    resetAwaitingPayment();
    setPaymentMethod(null);
    setStep(2);
  };

  return (
    <div className="w-full bg-slate-50 flex flex-col font-sans relative pt-6 pb-16">
      {planChangeDialog && chargedPaymentMethod && (
        <PlanChangeConfirmDialog
          open
          loading={planChangeLoading}
          fromPlanId={getChargedCheckoutPlanPeriod()?.planId || planId}
          fromPeriod={getChargedCheckoutPlanPeriod()?.period || period}
          toPlanId={planChangeDialog.nextPlanId}
          toPeriod={planChangeDialog.nextPeriod}
          paymentLabel={chargedPaymentMethod === 'boleto' ? 'boleto' : 'PIX'}
          onCancel={cancelPlanChangeDialog}
          onConfirm={() => void confirmPlanChangeDialog()}
        />
      )}
      <SEO 
        title="Checkout Seguro | RosTech" 
        description="Página de checkout seguro para o sistema RosTech."
        canonicalUrl="/checkout"
      />
      <div className="flex justify-end max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
        <div className="hidden sm:flex items-center text-sm font-semibold text-emerald-700 bg-emerald-50 px-4 py-2 rounded-full border border-emerald-200 gap-2 shadow-sm">
          <ShieldCheck className="w-5 h-5 text-emerald-600" /> Ambiente Seguro e Criptografado
        </div>
      </div>
      <main className="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-8 flex flex-col lg:flex-row gap-8 lg:gap-12 lg:items-start">
        
        {/* Left Column - Form */}
        <div className="flex-1">
          {/* Steps Progress */}
             <div className="flex items-center justify-between mb-10 relative">
             <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-slate-200 rounded-full z-0"></div>
             <div
               className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-blue-600 rounded-full z-0 transition-all duration-700 ease-out"
               style={{ width: step === 1 ? '0%' : step < 3 ? '50%' : '100%' }}
             />
             
             <div className="flex items-center gap-2 z-10 bg-slate-50 pr-2 sm:pr-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ring-4 ring-slate-50 ${step >= 1 ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' : 'bg-white border-2 border-slate-200 text-slate-400'}`}>
                   {step > 1 ? <Check className="w-5 h-5" /> : '1'}
                </div>
                <span className={`text-sm font-bold hidden sm:inline-block ${step >= 1 ? 'text-slate-900' : 'text-slate-400'}`}>Identificação</span>
             </div>
             <div className="flex items-center gap-2 z-10 bg-slate-50 px-2">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ring-4 ring-slate-50 ${step >= 2 ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' : 'bg-white border-2 border-slate-200 text-slate-400'}`}>
                   {step > 2 ? <Check className="w-5 h-5" /> : '2'}
                </div>
                <span className={`text-sm font-bold hidden sm:inline-block ${step >= 2 ? 'text-slate-900' : 'text-slate-400'}`}>Pagamento</span>
             </div>
             <div className="flex items-center gap-2 z-10 bg-slate-50 pl-2 sm:pl-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ring-4 ring-slate-50 ${step >= 3 ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' : 'bg-white border-2 border-slate-200 text-slate-400'}`}>
                   {step >= 3 ? <Check className="w-5 h-5" /> : '3'}
                </div>
                <span className={`text-sm font-bold hidden sm:inline-block ${step >= 3 ? 'text-slate-900' : 'text-slate-400'}`}>Confirmação</span>
             </div>
          </div>

          {/* Step 1: Identificação */}
          {step === 1 && (
            <div className="bg-white rounded-[2rem] shadow-xl shadow-slate-200/40 border border-slate-100 p-6 sm:p-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
               <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                 <User className="w-5 h-5 text-blue-600" /> Seus Dados
               </h2>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-1 md:col-span-2">
                     <label className="text-sm font-medium text-slate-700">Nome da empresa *</label>
                     <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className={inputElClass} placeholder="Oficina / Razão social" />
                  </div>
                  <div className="space-y-1">
                     <label className="text-sm font-medium text-slate-700">Nome Completo</label>
                     <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputElClass} placeholder="João da Silva" />
                  </div>
                  <div className="space-y-1">
                     <label className="text-sm font-medium text-slate-700">CPF / CNPJ</label>
                     <input 
                       type="text" 
                       className={inputWithFieldError(!!documentFieldError)} 
                       placeholder="000.000.000-00" 
                       value={documentId}
                       onChange={(e) => {
                         handleDocumentChange(e);
                         if (documentFieldError) setDocumentFieldError('');
                       }}
                       onBlur={handleDocumentBlur}
                       maxLength={18}
                     />
                     {documentFieldError ? (
                       <p className="text-xs font-medium text-red-600 mt-1">{documentFieldError}</p>
                     ) : null}
                  </div>
                  <div className="space-y-1">
                     <label className="text-sm font-medium text-slate-700">E-mail Comercial</label>
                     <input
                       type="email"
                       value={email}
                       onChange={(e) => {
                         setEmail(e.target.value);
                         if (emailFieldError) setEmailFieldError('');
                       }}
                       onBlur={handleEmailBlur}
                       className={inputWithFieldError(!!emailFieldError)}
                       placeholder="joao@minhaoficina.com.br"
                     />
                     {emailFieldError ? (
                       <p className="text-xs font-medium text-red-600 mt-1">{emailFieldError}</p>
                     ) : null}
                  </div>
                  <div className="space-y-1">
                     <label className="text-sm font-medium text-slate-700">Telefone / WhatsApp</label>
                     <input type="text" value={phone} onChange={handlePhoneChange} maxLength={15} className={inputElClass} placeholder="(00) 00000-0000" />
                  </div>
                  <div className="space-y-1">
                     <label className="text-sm font-medium text-slate-700">Senha</label>
                     <input 
                       type="password" 
                       value={password}
                       onChange={(e) => setPassword(e.target.value)}
                       className={inputElClass} 
                       placeholder="••••••••" 
                     />
                     <div className="mt-2 text-xs flex justify-between items-center h-4">
                       <div className="flex gap-1 flex-1 max-w-[120px]">
                         {[1, 2, 3, 4].map((level) => (
                           <div 
                             key={level} 
                             className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${level <= strength ? strengthColors[strength] : 'bg-slate-200'}`} 
                           />
                         ))}
                       </div>
                       <span className={`font-medium ${strength > 0 ? strengthColors[strength].replace('bg-', 'text-') : 'text-slate-400'}`}>
                         {strengthLabels[strength]}
                       </span>
                     </div>
                  </div>
                  <div className="space-y-1">
                     <label className="text-sm font-medium text-slate-700">Confirmação de Senha</label>
                     <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={inputElClass} placeholder="••••••••" />
                     <div className="mt-2 h-4 text-xs font-medium">
                       {confirmPassword.length > 0 && password !== confirmPassword && (
                         <span className="text-red-500 flex items-center gap-1">❌ As senhas não coincidem.</span>
                       )}
                       {confirmPassword.length > 0 && password === confirmPassword && (
                         <span className="text-emerald-600 flex items-center gap-1">✅ As senhas coincidem!</span>
                       )}
                     </div>
                  </div>
               </div>
               
               {step1Error && (
                 <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm font-medium flex items-start gap-2">
                   <div className="bg-white rounded-full p-0.5 shrink-0">❌</div> {step1Error}
                 </div>
               )}

               <div className="mt-8 pt-6 border-t border-slate-100 flex justify-end">
                  <Button size="lg" onClick={() => void handleNextStep1()} disabled={checkingStep1} className="group">
                     {checkingStep1 ? 'Verificando dados…' : 'Ir para o Pagamento'}{' '}
                     {!checkingStep1 ? (
                       <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                     ) : null}
                  </Button>
               </div>
            </div>
          )}

          {/* Step 2: Pagamento */}
          {step === 2 && (
            <div className="bg-white rounded-[2rem] shadow-xl shadow-slate-200/40 border border-slate-100 p-6 sm:p-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
               <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                 <Banknote className="w-5 h-5 text-blue-600" /> Forma de Pagamento
               </h2>
               
               {!paymentMethod && !showInlinePayment && !paymentAreaBusy && !isRestoringCheckout ? (
                 <p className="mb-4 text-center text-sm text-slate-500">
                   Clique em <strong className="text-slate-700">PIX</strong> ou <strong className="text-slate-700">Boleto</strong> para gerar o pagamento na hora
                   {SAAS_CHECKOUT_CREDIT_CARD_ENABLED ? (
                     <>
                       , ou em <strong className="text-slate-700">Cartão</strong> para preencher os dados.
                     </>
                   ) : (
                     <>.</>
                   )}
                 </p>
               ) : null}

               <div className={`grid gap-3 mb-8 ${SAAS_CHECKOUT_CREDIT_CARD_ENABLED ? 'grid-cols-3' : 'grid-cols-2'}`}>
                 {SAAS_CHECKOUT_CREDIT_CARD_ENABLED ? (
                 <button
                   type="button"
                   onClick={selectCardPayment}
                   className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all ${paymentMethod === 'credit_card' ? 'border-blue-600 bg-blue-50/50' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                 >
                   <CreditCard className={`w-6 h-6 mb-2 ${paymentMethod === 'credit_card' ? 'text-blue-700' : 'text-slate-400'}`} />
                   <span className={`text-xs sm:text-sm font-semibold ${paymentMethod === 'credit_card' ? 'text-blue-800' : 'text-slate-600'}`}>Cartão</span>
                 </button>
                 ) : null}
                 <button
                   type="button"
                   onClick={() => void activateInstantPayment('pix')}
                   disabled={submitting}
                   className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all ${paymentMethod === 'pix' ? 'border-blue-600 bg-blue-50/50' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                 >
                   <QrCode className={`w-6 h-6 mb-2 ${paymentMethod === 'pix' ? 'text-teal-600' : 'text-slate-400'}`} />
                   <span className={`text-xs sm:text-sm font-semibold ${paymentMethod === 'pix' ? 'text-blue-800' : 'text-slate-600'}`}>PIX</span>
                 </button>
                 <button
                   type="button"
                   onClick={() => void activateInstantPayment('boleto')}
                   disabled={submitting}
                   className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all ${paymentMethod === 'boleto' ? 'border-blue-600 bg-blue-50/50' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                 >
                   <FileText className={`w-6 h-6 mb-2 ${paymentMethod === 'boleto' ? 'text-slate-700' : 'text-slate-400'}`} />
                   <span className={`text-xs sm:text-sm font-semibold ${paymentMethod === 'boleto' ? 'text-blue-800' : 'text-slate-600'}`}>Boleto</span>
                 </button>
               </div>

               {SAAS_CHECKOUT_CREDIT_CARD_ENABLED && paymentMethod === 'credit_card' && !showInlinePayment && submitting && (
                 <div
                   className="min-h-[200px] rounded-xl border border-blue-100 bg-blue-50/40 flex flex-col items-center justify-center p-8 text-center mb-6"
                   role="status"
                   aria-live="polite"
                 >
                   <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-3" />
                   <p className="font-semibold text-slate-900">Processando pagamento com cartão…</p>
                 </div>
               )}

               {SAAS_CHECKOUT_CREDIT_CARD_ENABLED && paymentMethod === 'credit_card' && !showInlinePayment && !paymentAreaBusy && (
                 <div className="space-y-6 animate-in fade-in duration-300">
                    
                    {/* Interactive Real-Time Card Preview */}
                    <div className="relative w-full max-w-sm h-48 sm:h-[220px] bg-gradient-to-tr from-slate-900 via-slate-800 to-blue-900 rounded-2xl shadow-2xl overflow-hidden p-6 flex flex-col justify-between mx-auto mb-10 border border-white/10 text-white transition-all transform hover:scale-[1.02] duration-500">
                      {/* Decorative elements */}
                      <div className="absolute top-0 right-0 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl -mr-32 -mt-32"></div>
                      <div className="absolute bottom-0 left-0 w-40 h-40 bg-indigo-500/20 rounded-full blur-2xl -ml-20 -mb-20"></div>

                      <div className="flex justify-between items-start relative z-10">
                        <Cpu className="w-10 h-10 text-yellow-500/80 transform rotate-90" />
                        <div className="flex items-center gap-2">
                           <Wifi className="w-6 h-6 text-white/50 transform rotate-90" />
                           <div className="text-xl font-bold tracking-wider opacity-90 h-8 flex items-center">
                              {cardBrand ? cardBrand : <CreditCard className="w-6 h-6 opacity-30" />}
                           </div>
                        </div>
                      </div>
                      <div className="relative z-10 mt-auto">
                        <div className={`text-xl sm:text-2xl font-mono tracking-widest mb-4 transition-opacity ${cardNumber ? 'opacity-100 drop-shadow-md' : 'opacity-40'}`}>
                           {cardNumber || '•••• •••• •••• ••••'}
                        </div>
                        <div className="flex justify-between items-end text-sm sm:text-base font-medium">
                          <div className={`uppercase truncate max-w-[200px] transition-opacity tracking-wider ${cardName ? 'opacity-100' : 'opacity-40'}`}>
                             {cardName || 'NOME IMPRESSO'}
                          </div>
                          <div className={`transition-opacity font-mono tracking-widest ${cardExpiry ? 'opacity-100' : 'opacity-40'}`}>
                             {cardExpiry || 'MM/AA'}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-5">
                      <div className="space-y-1">
                         <label className="text-sm font-medium text-slate-700">Número do Cartão</label>
                         <input 
                           type="text" 
                           className={inputElClass} 
                           placeholder="0000 0000 0000 0000" 
                           value={cardNumber}
                           onChange={handleCardNumberChange}
                           maxLength={19}
                         />
                      </div>
                      <div className="space-y-1">
                         <label className="text-sm font-medium text-slate-700">Nome no Cartão</label>
                         <input 
                           type="text" 
                           className={inputElClass} 
                           placeholder="Conforme impresso" 
                           value={cardName}
                           onChange={(e) => setCardName(e.target.value)}
                           maxLength={50}
                         />
                      </div>
                      <div className="grid grid-cols-2 gap-5">
                        <div className="space-y-1">
                           <label className="text-sm font-medium text-slate-700">Validade</label>
                           <input 
                              type="text" 
                              className={inputElClass} 
                              placeholder="MM/AA" 
                              value={cardExpiry}
                              onChange={(e) => setCardExpiry(e.target.value)}
                              maxLength={5}
                           />
                        </div>
                        <div className="space-y-1">
                           <label className="text-sm font-medium text-slate-700">CVV</label>
                           <input 
                              type="password" 
                              className={inputElClass} 
                              placeholder="123" 
                              value={cardCvv}
                              onChange={(e) => setCardCvv(e.target.value)}
                              maxLength={4}
                           />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-5">
                        <div className="space-y-1">
                          <label className="text-sm font-medium text-slate-700">CEP titular *</label>
                          <input
                            type="text"
                            className={inputElClass}
                            placeholder="00000000"
                            value={holderPostalCode}
                            onChange={(e) => setHolderPostalCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                            maxLength={8}
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-sm font-medium text-slate-700">Nº endereço *</label>
                          <input
                            type="text"
                            className={inputElClass}
                            placeholder="123"
                            value={holderAddressNumber}
                            onChange={(e) => setHolderAddressNumber(e.target.value)}
                            maxLength={20}
                          />
                        </div>
                      </div>
                    </div>
                 </div>
               )}

               {step === 2 && isRestoringCheckout && !showInlinePayment && !submitting
                 ? renderPaymentAreaLoader(restorePaymentVariant)
                 : null}

               {paymentMethod === 'pix' && !showInlinePayment && paymentAreaBusy
                 ? renderPaymentAreaLoader('pix')
                 : null}

               {paymentMethod === 'boleto' && !showInlinePayment && paymentAreaBusy
                 ? renderPaymentAreaLoader('boleto')
                 : null}

               {showInlinePayment ? (
                 <PixPaymentInline
                   active={showInlinePayment}
                   token={continuationToken}
                   initialPix={pixPayload}
                   paymentMethod={paymentMethod === 'boleto' ? 'boleto' : 'pix'}
                   bankSlipUrl={bankSlipUrl}
                   invoiceUrl={invoiceUrl}
                   onConfirmed={() => {
                     goToCompletionStep({
                       paymentId: lastSignupPaymentId,
                       subscriptionId: lastSignupSubscriptionId,
                     });
                   }}
                   onError={(msg) => {
                     resetAwaitingPayment();
                     setStep2Error(msg);
                   }}
                 />
               ) : null}

               {step2Error && (
                 <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm font-medium flex items-start gap-2">
                   <div className="bg-white rounded-full p-0.5 shrink-0">❌</div> {step2Error}
                 </div>
               )}

               <div className="mt-8 pt-6 border-t border-slate-100 flex flex-col sm:flex-row justify-between items-center sm:items-start gap-4">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => {
                      if (pixAwaitingPayment) {
                        resetAwaitingPayment();
                      } else {
                        setStep(1);
                      }
                    }}
                    className="text-slate-600 w-full sm:w-auto order-last sm:order-first"
                  >
                     Voltar
                  </Button>
                  {!showInlinePayment ? (
                  <div className="flex flex-col items-center sm:items-end w-full sm:w-auto">
                    <Button
                      size="lg"
                      onClick={() => void handleNextStep2()}
                      disabled={submitting}
                      className="bg-blue-600 hover:bg-blue-700 text-white border-0 shadow-lg shadow-blue-600/30 w-full sm:w-auto"
                    >
                      {submitting ? 'Processando…' : 'Finalizar assinatura'}
                    </Button>
                    <p className="mt-4 text-[11px] text-slate-500 text-center sm:text-right leading-relaxed max-w-xs">
                      Ao assinar, você concorda com nossos{" "}
                      <Link to="/termos" className="font-medium text-blue-700 hover:text-blue-600" target="_blank">Termos de Uso</Link>{" "}
                      e{" "}
                      <Link to="/privacidade" className="font-medium text-blue-700 hover:text-blue-600" target="_blank">Política de Privacidade</Link>.
                    </p>
                  </div>
                  ) : (
                    <p className="text-sm text-slate-600 text-center sm:text-right max-w-xs">
                      Aguardando confirmação. Troque a aba acima para usar outro meio de pagamento.
                    </p>
                  )}
               </div>
            </div>
          )}

          {/* Step 3: Confirmação (visual original, dados reais da API/Asaas) */}
          {step === 3 && (
            <div className="bg-white rounded-[2rem] shadow-xl shadow-slate-200/40 border border-slate-100 p-8 md:p-12 text-center animate-in zoom-in-95 duration-500">
              <div className="w-24 h-24 bg-emerald-100 text-emerald-600 rounded-[2rem] shadow-sm flex items-center justify-center mx-auto mb-8 transform -rotate-6">
                <CheckCircle className="w-12 h-12 transform rotate-6" />
              </div>
              <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-4">Pedido Concluído!</h2>
              <p className="text-slate-500 text-lg mb-8 max-w-md mx-auto">
                Sua assinatura do <strong>{selectedPlan.name}</strong> ({periodLabels[period]}) foi confirmada.
                Pagamento validado com o Asaas.
              </p>

              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 mb-10 max-w-sm mx-auto text-left shadow-sm">
                <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-1">Total</p>
                <p className="text-4xl font-extrabold text-slate-900 mb-6 tracking-tight">
                  <span className="text-2xl mr-1 text-slate-400">R$</span>
                  {total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <div className="space-y-3 mb-2">
                  <p className="text-sm font-semibold text-slate-700 flex justify-between gap-4">
                    Forma de Pagamento:{' '}
                    <span className="font-bold text-slate-900 text-right">
                      {paymentMethod === 'credit_card'
                        ? 'Cartão de Crédito'
                        : paymentMethod === 'pix'
                          ? 'PIX'
                          : 'Boleto'}
                    </span>
                  </p>
                  <p className="text-sm font-semibold text-slate-700 flex justify-between gap-4">
                    ID do Pedido:{' '}
                    <span className="font-bold font-mono text-slate-900 text-right">{completedOrderRef}</span>
                  </p>
                </div>
              </div>

              <Button
                size="lg"
                className="w-full max-w-sm mx-auto shadow-lg shadow-blue-600/30 group"
                onClick={() => navigate('/login?cadastro=ok')}
              >
                Acessar meu Painel <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <p className="text-xs text-slate-400 mt-4">Use o e-mail e a senha cadastrados no passo 1.</p>
            </div>
          )}

        </div>

        {/* Right Column - Resumo do Pedido */}
        <div className="w-full lg:w-[400px] shrink-0 order-first lg:order-last">
          <div className="bg-white text-slate-900 rounded-[2rem] shadow-xl shadow-slate-200/40 p-6 sm:p-8 md:p-10 sticky top-24 border border-slate-100">
             <h3 className="font-bold text-xl mb-6 pb-5 border-b border-slate-100 flex items-center justify-between">
               Resumo 
               <ShieldCheck className="w-6 h-6 text-emerald-600" />
             </h3>

             {plansLoading && (
               <div className="flex items-center justify-center gap-2 text-sm text-slate-500 mb-4 py-6">
                 <Loader2 className="w-5 h-5 animate-spin" />
                 Carregando planos…
               </div>
             )}
             
             <div className={`grid grid-cols-2 gap-3 mb-6 pb-6 border-b border-slate-100 ${plansLoading ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="space-y-1">
                   <label className="text-[10px] font-bold tracking-wider text-slate-500 uppercase">Plano</label>
                   <select 
                     value={planId} 
                     onChange={(e) => requestPlanOrPeriodChange(e.target.value, period)}
                     disabled={planChangeLoading || plansLoading || plans.length === 0}
                     className="w-full h-11 px-2 text-ellipsis border border-slate-200 rounded-lg text-sm font-bold text-slate-900 bg-slate-50 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-colors"
                   >
                     {plans.map(p => (
                       <option key={p.id} value={p.id}>{p.name}</option>
                     ))}
                   </select>
                </div>
                <div className="space-y-1">
                   <label className="text-[10px] font-bold tracking-wider text-slate-500 uppercase">Ciclo</label>
                   <select 
                     value={period} 
                     onChange={(e) => requestPlanOrPeriodChange(planId, e.target.value)}
                     disabled={planChangeLoading || plansLoading}
                     className="w-full h-11 px-2 border border-slate-200 rounded-lg text-sm font-bold text-slate-900 bg-slate-50 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-colors"
                   >
                     {Object.entries(periodLabels).map(([key, label]) => (
                       <option key={key} value={key}>{label}</option>
                     ))}
                   </select>
                </div>
             </div>

             <div className="mb-6">
                <div className="flex justify-between items-start mb-1">
                   <h4 className="font-bold text-lg">{selectedPlan.name}</h4>
                   <span className="font-extrabold text-3xl tracking-tight text-slate-900">
                     <span className="text-xl mr-1 text-slate-500">R$</span>
                     {total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                   </span>
                </div>
                <p className="text-sm border-l-2 border-emerald-500 pl-3 text-emerald-600 font-medium">Plano {periodLabels[period]}</p>
             </div>

             {discount > 0 && (
                <div className="mb-6 text-emerald-700 bg-emerald-50 p-4 rounded-xl border border-emerald-100 shadow-sm">
                   <div className="flex justify-between items-center mb-1">
                     <span className="text-sm font-bold flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-emerald-600" /> Promocional</span>
                     <span className="text-sm font-bold text-emerald-700">
                       - R$ {((selectedPlan.price * (period === 'mensal' ? 1 : period === 'trimestral' ? 3 : period === 'semestral' ? 6 : 12)) * discount).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                     </span>
                   </div>
                   <div className="text-[11.5px] font-medium text-emerald-600/90 leading-relaxed mt-2 pt-2 border-t border-emerald-200/50">
                      Desconto válido para o 1º {period === 'trimestral' ? 'trimestre' : period === 'semestral' ? 'semestre' : 'ano'}. Após, R$ {selectedPlan.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês.
                   </div>
                </div>
             )}

             <ul className="space-y-4 mb-8 text-sm text-slate-600 pb-8 border-b border-slate-100 font-medium">
                <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" /> Cobrança {period === 'mensal' ? 'mensal' : period === 'trimestral' ? 'a cada 3 meses' : period === 'semestral' ? 'a cada 6 meses' : 'anual'} renovada automaticamente a preço normal.</li>
                <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" /> Cancele quando quiser. Sem taxas escondidas, com o controle em suas mãos.</li>
             </ul>

             <div className="flex items-center gap-2 text-xs text-slate-500 font-bold justify-center bg-slate-50 border border-slate-100 p-3 rounded-xl shadow-sm">
                <Lock className="w-4 h-4 text-slate-400" /> Transação Criptografada (SSL)
             </div>
          </div>
        </div>

      </main>

    </div>
  );
}
